import { CustomImageType } from "@bared/groq";
import { useAnalytics } from "@hooks/useAnalytics";
import { useLocalisation } from "@hooks/useLocalisation";
import { useRoutes } from "@hooks/useRoutes";
import { type ComponentProps, type ResolvedLink } from "@ts/components";
import {
  BannerSwiperContent,
  type SectionBanner,
  type Styles,
} from "@ts/sanity";
import React, { useCallback, type FC } from "react";
import { ZodVoidDef } from "zod";

type BannerCarousel = Omit<
  BannerSwiperContent,
  "desktopImage" | "mobileImage" | "textColor"
> & {
  desktopImage: CustomImageType;
  desktopImagelink?: ResolvedLink;
  desktopVideoUrl?: string;
  mobileImage: CustomImageType;
  mobileVideoUrl?: string;
  textColor: "primary" | "lightest";
};

export type BannerInputProps = ComponentProps &
  Omit<SectionBanner, "styles"> & {
    styles: Styles[] | null;
    bannerContent?: BannerSwiperContent[];
  };

export type BannerOutputProps = Omit<
  BannerInputProps,
  "imageLink" | "mobileImageLink" | "bannerContent"
> & {
  bannerContent?: BannerCarousel[];
  handleBannerClick?: (url?: string) => void;
  handlePromoClick?: () => ZodVoidDef;
};

export const withBanner =
  (Component: FC<BannerOutputProps>) =>
  ({ name = "Banner", analytics = {}, bannerContent }: BannerInputProps) => {
    const { linkResolver } = useRoutes();
    const { navigate } = useLocalisation();

    const { trackPromoClick } = useAnalytics();

    const bannerCarousel = bannerContent?.map((item) => {
      const desktopVideoUrl = item.desktopVideo?.url;
      const mobileVideoUrl = item.mobileVideo?.url;
      const desktopImagelink = linkResolver(item.desktopImage?.imagelink?.[0]);

      return {
        ...item,
        desktopImagelink,
        desktopVideoUrl,
        mobileVideoUrl,
      };
    });

    const handleBannerClick = useCallback(
      (url?: string) => {
        if (!url) return;

        navigate(url);
      },
      [navigate],
    );

    const handlePromoClick = useCallback(() => {
      if (!analytics) {
        return;
      }
      trackPromoClick(analytics);
    }, [analytics, trackPromoClick]);

    Component.displayName = name;
    return (
      <Component
        bannerContent={bannerCarousel}
        handleBannerClick={handleBannerClick}
        handlePromoClick={handlePromoClick}
      />
    );
  };
